import * as _ from "underscore";

import * as experiment from "experiment";
import * as localAPI from "localAPI";
import readlangClient from "readlangClient";

/**
 * Minimum number of words between review sessions.
 * (This constraint can be broken in order to that sections end with a review session)
 */
const gapBetweenReviewSessions = 300;

interface PositionInfo {
  position: number;
  state: undefined | "generated" | "completed";
}

let sessionStates:
  | {
      bookID: string;
      sessions: PositionInfo[];
    }
  | undefined = undefined;

export const reset = () => {
  sessionStates = undefined;
};

/**
 * Returns true if the user currently has access to the reader review feature in their
 * current learning language.
 */
export const hasAccess = async (models: any) => {
  return !models.bookModel.has("youTubeID") && !(await localAPI.isLocalMode());
};

/** Returns true if the reader review buttons should be shown */
export const isActive = (models: any) => {
  if (!hasAccess(models)) {
    return false;
  }

  if (models.user.has("readerReview")) {
    // User has explicitly enabled or disabled reader review
    return models.user.get("readerReview") === "show";
  }

  // Return default value based on experiment condition
  return (
    models.user.hasPremiumAccess() ||
    (!models.user.isLocalMode() &&
      experiment.getConditionAndTreat("readerReview2") === "experiment")
  );
};

export const listReviewSessions = async (
  bookID: string
): Promise<PositionInfo[]> => {
  if (sessionStates?.bookID === bookID) {
    return sessionStates.sessions;
  }

  const { data } = await readlangClient.request(
    "GET",
    "/api/readerReview/sessionStates",
    {
      bookID,
    }
  );

  sessionStates = {
    bookID,
    sessions: data?.sessions ?? [],
  };

  return sessionStates.sessions;
};

/**
 * This is designed to be called for every paragraph break in a book.
 *
 * @returns `true` if a review session should be added at this point in the book.
 */
const shouldAddReviewSession = async (
  bookID: string,
  toPosition: number,
  totalWords: number,
  bookSections: {
    name: string;
    start: number;
    children: {
      name: string;
      start: number;
    }[];
  }[]
): Promise<boolean> => {
  const reviewSessions = await listReviewSessions(bookID);

  if (toPosition >= totalWords - 1) {
    // We're at the end of the book, so we should add a review session
    return true;
  }

  /** The last review session before `toPosition` within the book (if one exists) */
  const previousReviewSession = _.last(
    reviewSessions.filter((info) => info.position <= toPosition)
  );

  if (previousReviewSession?.position === toPosition) {
    // We are at the end of a review session, so return true
    // XXX Should return the actual session data here?
    return true;
  }

  const flattenedBookSectionPositions = [];
  for (const section of bookSections) {
    flattenedBookSectionPositions.push(section.start);
    for (const subsection of section.children) {
      flattenedBookSectionPositions.push(subsection.start);
    }
  }

  const nextSectionPosition = flattenedBookSectionPositions.find(
    (position) => position > toPosition
  );

  const gapSinceLastReviewSession =
    toPosition - (previousReviewSession?.position ?? 0);

  if (
    toPosition === nextSectionPosition - 1 &&
    // Don't add a review session if the section is too short
    gapSinceLastReviewSession > 12
  ) {
    // We are at the end of a section, so we should add a review session
    return true;
  }

  if (
    nextSectionPosition &&
    nextSectionPosition - toPosition < gapBetweenReviewSessions / 3
  ) {
    // We are close to the next section, so wait until the section break to add a review session
    return false;
  }

  if (gapSinceLastReviewSession > gapBetweenReviewSessions) {
    // We are far enough from the previous review session, so we should add one
    return true;
  }

  return false;
};

const addReviewSession = async (
  bookID: string,
  toPosition: number
): Promise<void> => {
  const reviewSessions = await listReviewSessions(bookID);

  if (reviewSessions.some((info) => info.position === toPosition)) {
    // no need to insert anything
    return;
  }

  sessionStates.sessions.push({ position: toPosition, state: undefined });
  sessionStates.sessions.sort((a, b) => a.position - b.position);

  // TODO: should we push this to the server? Or maybe it isn't necessary?
};

export const spliceInReviewSessionButtons = async (
  bookID: string,
  html: string,
  toPosition: number,
  totalWords: number,
  sections: any,
  // Used to avoid adding a duplicate review session immediately after the last one in the case
  // where there are multiple line-breaks with no words.
  justAddedBeforePosition?: number
) => {
  const endParagraphTag = "</p>";

  // Find the first paragraph boundary after the nextBoundaryOffset
  let nextParagraphOffset = html.indexOf(endParagraphTag);

  /**
   * @param {number} afterCharacterOffset
   *
   * @returns {number | undefined} the next position after the given character offset, or
   *                               undefined if it doesn't exist.
   */
  const getNextPosition = (afterCharacterOffset) => {
    const spanRegExp = /<span data-p=(\d+)>/g;

    const match = spanRegExp.exec(html.substring(afterCharacterOffset));
    if (match) {
      return parseInt(match[1], 10);
    }
    return undefined;
  };

  let nextPosition = getNextPosition(nextParagraphOffset);

  while (
    nextPosition !== undefined &&
    (nextPosition === justAddedBeforePosition ||
      !(await shouldAddReviewSession(
        bookID,
        nextPosition - 1,
        totalWords,
        sections
      )))
  ) {
    nextParagraphOffset = html.indexOf(
      endParagraphTag,
      nextParagraphOffset + 1
    );
    nextPosition =
      nextParagraphOffset === -1
        ? undefined
        : getNextPosition(nextParagraphOffset);
  }

  if (
    // If we end with the last paragraph, add the section break after it
    nextPosition === undefined &&
    nextParagraphOffset === html.length - endParagraphTag.length &&
    toPosition >= totalWords - 1
  ) {
    nextPosition = totalWords;
  }

  if (
    // Splice in the section break after the paragraph boundary
    nextPosition !== undefined &&
    nextPosition > 0 &&
    nextParagraphOffset !== -1
  ) {
    justAddedBeforePosition = nextPosition;
    html =
      html.substring(0, nextParagraphOffset + endParagraphTag.length) +
      `<div class="sectionBreak">
        <div class="sectionBreakLine"></div>
        <a href="#" title="Show summary of previous section" class="startReviewSession" data-to="${nextPosition - 1}"
        >?</a>
        <div class="sectionBreakLine"></div>
      </div>` +
      html.substring(nextParagraphOffset + endParagraphTag.length);

    addReviewSession(bookID, nextPosition - 1);

    // Recurse to find the next paragraph boundary
    html =
      html.substring(0, nextParagraphOffset + endParagraphTag.length) +
      (await spliceInReviewSessionButtons(
        bookID,
        html.substring(nextParagraphOffset + endParagraphTag.length),
        nextPosition,
        totalWords,
        sections,
        justAddedBeforePosition
      ));
  }

  return html;
};
